/**
 * Role Constants and Utilities
 * 
 * Single source of truth for all role-related values and checks.
 * Any role validation MUST use these constants and functions.
 */

// Official role values (as stored in database)
export const UserRole = {
  // Recruiting roles
  RECRUITER: 'recruiter',
  LEAD_RECRUITER: 'lead_recruiter',
  RECRUITER_SUPERVISOR: 'recruiter_supervisor',
  
  // Sales/Account roles
  ACCOUNT_MANAGER: 'account_manager',
  ACCOUNT_EXEC: 'account_exec',
  
  // Admin roles
  JUNO_ADMIN: 'junoadmin',
  
  // Legacy/deprecated roles (for backwards compatibility)
  LEGACY_LEAD: 'lead',
  LEGACY_ADMIN: 'admin',
};

// Role categories for grouping
export const RoleCategory = {
  RECRUITING_STAFF: [
    UserRole.RECRUITER,
    UserRole.LEAD_RECRUITER,
    UserRole.RECRUITER_SUPERVISOR,
  ],
  
  LEAD_ROLES: [
    UserRole.LEAD_RECRUITER,
    UserRole.RECRUITER_SUPERVISOR,
  ],
  
  ACCOUNT_ROLES: [
    UserRole.ACCOUNT_MANAGER,
    UserRole.ACCOUNT_EXEC,
  ],
  
  ADMIN_ROLES: [UserRole.JUNO_ADMIN],
  
  // Roles that can have industry assignments
  INDUSTRY_ENABLED_ROLES: [
    UserRole.RECRUITER,
    UserRole.LEAD_RECRUITER,
    UserRole.RECRUITER_SUPERVISOR,
    UserRole.ACCOUNT_MANAGER,
    UserRole.ACCOUNT_EXEC,
  ],
};

/**
 * Normalize legacy role names to current standard
 * @param {string} role - Role value to normalize
 * @returns {string} Normalized role value
 */
export const normalizeRole = (role) => {
  const roleMapping = {
    [UserRole.LEGACY_LEAD]: UserRole.LEAD_RECRUITER,
    [UserRole.LEGACY_ADMIN]: UserRole.JUNO_ADMIN,
    'lead': UserRole.LEAD_RECRUITER,
    'admin': UserRole.JUNO_ADMIN,
    'Lead Recruiter': UserRole.LEAD_RECRUITER,
    'sales': UserRole.ACCOUNT_EXEC,
  };
  return roleMapping[role] || role;
};

/**
 * Check if role is a recruiting role
 * @param {string} role - Role to check
 * @returns {boolean}
 */
export const isRecruitingRole = (role) => {
  return RoleCategory.RECRUITING_STAFF.includes(role);
};

/**
 * Check if role is a lead/management role (handles legacy 'lead' too)
 * @param {string} role - Role to check
 * @returns {boolean}
 */
export const isLeadRole = (role) => {
  return RoleCategory.LEAD_ROLES.includes(role) || role === UserRole.LEGACY_LEAD;
};

/**
 * Check if role is specifically lead_recruiter (handles legacy 'lead' too)
 * @param {string} role - Role to check
 * @returns {boolean}
 */
export const isLeadRecruiter = (role) => {
  return role === UserRole.LEAD_RECRUITER || role === UserRole.LEGACY_LEAD;
};

/**
 * Check if role is an admin role
 * @param {string} role - Role to check
 * @returns {boolean}
 */
export const isAdminRole = (role) => {
  return RoleCategory.ADMIN_ROLES.includes(role) || role === UserRole.LEGACY_ADMIN;
};

/**
 * Check if role is an account management role
 * @param {string} role - Role to check
 * @returns {boolean}
 */
export const isAccountManagement = (role) => {
  return RoleCategory.ACCOUNT_ROLES.includes(role);
};

/**
 * Check if user is a team lead (can manage a team)
 * @param {string} role - Role to check
 * @returns {boolean}
 */
export const isTeamLead = (role) => {
  return isLeadRole(role) || role === UserRole.ACCOUNT_MANAGER;
};

/**
 * Get user's roles array (supports both single role and multi-role users)
 * @param {Object} user - User object
 * @returns {Array} Array of roles
 */
export const getUserRoles = (user) => {
  if (!user) return [];
  // Support both 'roles' array and single 'role' field
  if (user.roles && Array.isArray(user.roles)) {
    return user.roles;
  }
  if (user.role) {
    return [user.role];
  }
  return [];
};

/**
 * Check if user can view all clients (admin or account management)
 * @param {Object} user - User object
 * @returns {boolean}
 */
export const canViewAllClients = (user) => {
  if (!user) return false;
  const roles = getUserRoles(user);
  return roles.some(role => isAdminRole(role) || isAccountManagement(role));
};

/**
 * Check if user has a specific role
 * @param {Object} user - User object
 * @param {string} roleToCheck - Role to check for
 * @returns {boolean}
 */
export const hasRole = (user, roleToCheck) => {
  if (!user) return false;
  const roles = getUserRoles(user);
  return roles.includes(roleToCheck);
};

/**
 * Check if user is an admin
 * @param {Object} user - User object
 * @returns {boolean}
 */
export const isAdmin = (user) => {
  if (!user) return false;
  const roles = getUserRoles(user);
  return roles.some(role => isAdminRole(role));
};

/**
 * Get team access level for user
 * @param {Object} user - User object
 * @returns {string} Access level: 'admin', 'lead', 'member', 'none'
 */
export const getTeamAccessLevel = (user) => {
  if (!user) return 'none';
  const roles = getUserRoles(user);
  
  if (roles.some(role => isAdminRole(role))) {
    return 'admin';
  }
  if (roles.some(role => isTeamLead(role))) {
    return 'lead';
  }
  if (roles.some(role => isRecruitingRole(role))) {
    return 'member';
  }
  return 'none';
};

/**
 * Check if role can have industry assignments
 * @param {string} role - Role to check
 * @returns {boolean}
 */
export const canHaveIndustries = (role) => {
  return RoleCategory.INDUSTRY_ENABLED_ROLES.includes(role);
};

/**
 * Get human-readable display name for role
 * @param {string} role - Role value
 * @returns {string} Display name
 */
export const getRoleDisplayName = (role) => {
  const displayNames = {
    [UserRole.RECRUITER]: 'Recruiter',
    [UserRole.LEAD_RECRUITER]: 'Lead Recruiter',
    [UserRole.RECRUITER_SUPERVISOR]: 'Recruiting Supervisor',
    [UserRole.ACCOUNT_MANAGER]: 'Account Manager',
    [UserRole.ACCOUNT_EXEC]: 'Account Executive',
    [UserRole.JUNO_ADMIN]: 'Administrator',
  };
  return displayNames[role] || role.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
};

/**
 * Get CSS class for role badge
 * @param {string} role - Role value
 * @returns {string} CSS classes
 */
export const getRoleBadgeClass = (role) => {
  if (isLeadRecruiter(role)) {
    return 'bg-blue-100 text-blue-800';
  }
  if (role === UserRole.RECRUITER) {
    return 'bg-green-100 text-green-800';
  }
  if (role === UserRole.RECRUITER_SUPERVISOR) {
    return 'bg-purple-100 text-purple-800';
  }
  if (RoleCategory.ACCOUNT_ROLES.includes(role)) {
    return 'bg-orange-100 text-orange-800';
  }
  if (isAdminRole(role)) {
    return 'bg-red-100 text-red-800';
  }
  return 'bg-gray-100 text-gray-800';
};

/**
 * Check if a role can be added to a team led by another role
 * @param {string} teamLeadRole - Role of the team lead
 * @param {string} memberRole - Role of the member to add
 * @returns {boolean}
 */
export const canAddToTeam = (teamLeadRole, memberRole) => {
  const allowedMembers = {
    [UserRole.RECRUITER_SUPERVISOR]: [UserRole.LEAD_RECRUITER, UserRole.RECRUITER],
    [UserRole.LEAD_RECRUITER]: [UserRole.RECRUITER],
    [UserRole.ACCOUNT_MANAGER]: [UserRole.RECRUITER, UserRole.LEAD_RECRUITER],
  };
  
  // Handle legacy lead role
  const normalizedLeadRole = normalizeRole(teamLeadRole);
  return (allowedMembers[normalizedLeadRole] || []).includes(memberRole);
};

/**
 * Filter users by role (handles legacy role names)
 * @param {Array} users - Array of user objects
 * @param {string} roleFilter - Role to filter by
 * @returns {Array} Filtered users
 */
export const filterUsersByRole = (users, roleFilter) => {
  if (roleFilter === 'all') return users;
  
  return users.filter(user => {
    // Handle exact match
    if (user.role === roleFilter) return true;
    
    // Handle lead_recruiter filter matching legacy 'lead'
    if (roleFilter === UserRole.LEAD_RECRUITER && user.role === UserRole.LEGACY_LEAD) {
      return true;
    }
    if (roleFilter === UserRole.LEGACY_LEAD && user.role === UserRole.LEAD_RECRUITER) {
      return true;
    }
    
    // Handle admin filter
    if (roleFilter === UserRole.JUNO_ADMIN && user.role === UserRole.LEGACY_ADMIN) {
      return true;
    }
    if (roleFilter === UserRole.LEGACY_ADMIN && user.role === UserRole.JUNO_ADMIN) {
      return true;
    }
    
    return false;
  });
};
